import { contentHash } from "../content/rules";
import { PHASE, type InputBits, type MatchState, type SimEvent } from "../sim";

/**
 * «Чёрный ящик» боя на одном ПК (консультант 05.11.2026): ввод обоих игроков каждого кадра и начатые приёмы. Мир
 * детерминирован — по журналу бой переигрывается точь-в-точь (tools/capture/capture.mjs --journal). Журнал уходит
 * приёмнику на этом ПК (server/journal, 127.0.0.1:8788) каждые 5 с, на каждом конце раунда и при уходе со страницы
 * (иначе конец боя терялся: Джон 05.11.2026 — «почему обрывается видео перед тем как он наносит последний удар?»).
 * Ввод хранится повторами подряд [число, сколько кадров]: журнал боя — единицы КБ, его успевает унести и закрытие вкладки.
 */
const FLUSH_EVERY = 300;
const ENDPOINT = "http://127.0.0.1:8788/journal";
/** Ввод кадра одним числом: игрок 1 — младшие 13 бит, игрок 2 — старшие. */
export const PLAYER_BITS = 8192;

export interface Journal {
  readonly version: 2;
  readonly id: string;
  readonly startedAt: string;
  readonly seed: number;
  readonly content: string;
  readonly bots: readonly boolean[];
  readonly meter: number;
  /** Приёмы наборов обоих бойцов (id) — счётчик видит и те, что за бой ни разу не вышли (tools/check/abilities.py). */
  readonly kits?: readonly (readonly string[])[];
  /** Сколько кадров записано. */
  frames: number;
  /** Ввод повторами: [число, кадров, число, кадров, …]. */
  readonly runs: number[];
  readonly events: [number, number, string, string][];
}

/** Ввод журнала по кадрам (для переигровки). */
export function journalInputs(journal: { runs?: number[]; inputs?: number[] }): number[] {
  if (journal.inputs) return journal.inputs;
  const out: number[] = [];
  const runs = journal.runs ?? [];
  for (let index = 0; index + 1 < runs.length; index += 2)
    for (let count = 0; count < (runs[index + 1] ?? 0); count++) out.push(runs[index] ?? 0);
  return out;
}

export class BlackBox {
  private readonly journal: Journal;
  private sent = 0;

  constructor(
    seed: number,
    bots: readonly boolean[],
    meter: number,
    kits: readonly (readonly string[])[],
    target?: Window,
  ) {
    const started = new Date();
    this.journal = {
      version: 2,
      id: `${started
        .toISOString()
        .replace(/[^0-9]/g, "")
        .slice(0, 14)}-${String(seed)}`,
      startedAt: started.toISOString(),
      seed,
      content: contentHash(),
      bots,
      meter,
      kits,
      frames: 0,
      runs: [],
      events: [],
    };
    // уход со страницы (закрыли вкладку, свернули) — журнал уносит sendBeacon, ему не нужен ответ
    const leave = (): void => {
      this.flush(true);
    };
    target?.addEventListener("pagehide", leave);
    target?.document.addEventListener("visibilitychange", () => {
      if (target.document.visibilityState === "hidden") leave();
    });
  }

  record(inputs: readonly [InputBits, InputBits], events: readonly SimEvent[], state: MatchState): void {
    const packed = inputs[0] + inputs[1] * PLAYER_BITS;
    const runs = this.journal.runs;
    const last = runs.length - 2;
    if (last >= 0 && runs[last] === packed) runs[last + 1] = (runs[last + 1] ?? 0) + 1;
    else runs.push(packed, 1);
    this.journal.frames++;
    for (const event of events)
      if (event.kind === "ability" || event.kind === "hit" || event.kind === "block" || event.kind === "ko")
        this.journal.events.push([state.frame, event.who, event.kind, event.ability ?? ""]);
    const roundOver = (state.phase === PHASE.ko || state.phase === PHASE.timeUp) && state.phaseFrame === 1;
    if (roundOver || this.journal.frames - this.sent >= FLUSH_EVERY) this.flush();
  }

  /** Отправить журнал целиком (перезаписывает файл боя); leaving — уход со страницы, отправка без ответа. */
  flush(leaving = false): void {
    // на общей ссылке приёмника нет: запрос на 127.0.0.1 у чужого игрока — лишнее окно разрешения в браузере
    if (location.hostname.endsWith(".workers.dev") || location.hostname.endsWith(".pages.dev")) return;
    this.sent = this.journal.frames;
    const body = JSON.stringify(this.journal);
    try {
      if (leaving && typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "text/plain" }));
        return;
      }
      void fetch(ENDPOINT, {
        method: "POST",
        body,
        headers: { "Content-Type": "text/plain" },
        keepalive: body.length < 60_000,
      }).catch(() => undefined);
    } catch {
      /* нет сети или приёмника — бой идёт без журнала */
    }
  }
}
